<script setup lang="ts">
/**
 * Standard error surface for failed list / detail queries.
 *
 * Shows: short title + human message, with an expandable "Details"
 * block that exposes the underlying ApiError (kind, code, status, url,
 * raw response body). A "Copy" button puts the whole debug payload on
 * the clipboard as JSON — handy to paste into a bug report.
 *
 * Replaces the duplicated `<AppCard v-else-if="isError">` blocks in
 * Dishes / Categories / Tags / Media / Super-categories views.
 *
 * **When NOT to use:**
 * - Connectivity issues (offline, slow link). Those are handled
 *   globally by `AppNetworkBanner`; surfacing the same condition in
 *   every list view duplicates the message.
 * - Mutation failures (Save, Delete). Those belong in a toast via
 *   `useToast().error('…', messageOf(err))` so the user keeps the
 *   form context. AppErrorCard is for queries that produce nothing
 *   else to interact with.
 * - Empty results — that's `AppEmptyState`. An empty list isn't an
 *   error.
 * - 401 unauthorized. The api client routes that through the refresh
 *   coordinator and (on hard failure) to the auth-lost redirect.
 *   Showing an error card with "Unauthorized" leaves the user
 *   stranded; they need to log back in.
 */
import { computed, ref } from 'vue'
import { ChevronDown, Copy } from '@lucide/vue'

import { ApiError } from '@/shared/api'
import { cn } from '@/shared/lib/cn'
import AppButton from './AppButton.vue'
import AppCard from './AppCard.vue'

interface Props {
  /** What was being loaded — e.g. "Failed to load items". */
  title: string
  /** The error from useQuery/useMutation. Typically `ApiError`. */
  error: unknown
  /** Show / hide the Try again button (default true). */
  retryable?: boolean
}
const props = withDefaults(defineProps<Props>(), { retryable: true })
const emit = defineEmits<{ retry: [] }>()

const expanded = ref(false)
const copied = ref(false)

const isApi = computed(() => props.error instanceof ApiError)
const apiError = computed<ApiError | null>(() => (isApi.value ? (props.error as ApiError) : null))

const message = computed<string>(() => {
  if (apiError.value) return apiError.value.message
  if (props.error instanceof Error) return props.error.message
  return 'Unknown error'
})

const debugPayload = computed(() => {
  const e = apiError.value
  if (e) {
    return {
      kind: e.kind,
      code: e.code,
      status: e.status,
      message: e.message,
      method: e.method ?? null,
      url: e.url ?? null,
      hint: e.hint ?? null,
      retryAfter: e.retryAfter ?? null,
      details: e.details ?? null,
    }
  }
  if (props.error instanceof Error) {
    return { name: props.error.name, message: props.error.message, stack: props.error.stack }
  }
  return { error: String(props.error) }
})

const debugJson = computed(() => JSON.stringify(debugPayload.value, null, 2))

async function copyDebug() {
  try {
    await navigator.clipboard.writeText(debugJson.value)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 1500)
  } catch {
    // Clipboard API can fail (non-secure context, permissions). Fall
    // through silently — the user can still select the text manually.
  }
}
</script>

<template>
  <AppCard padding="md" class="border-destructive/40">
    <h2 class="text-sm font-semibold text-destructive">{{ title }}</h2>
    <p class="mt-1 text-sm text-muted-foreground">{{ message }}</p>

    <div class="mt-3 flex flex-wrap items-center gap-2">
      <!-- Try again is a real button (h-9 ≈ 36px desktop, scales taller
           on mobile via min-h on small variants) so the tap target
           meets the 44px guideline. The earlier text-link variant was
           a sub-20px hit on touch. -->
      <AppButton v-if="retryable" size="sm" variant="outline" @click="emit('retry')">
        Try again
      </AppButton>
      <AppButton size="sm" variant="ghost" :aria-expanded="expanded" @click="expanded = !expanded">
        <template #icon-left>
          <ChevronDown
            :class="cn('size-4 transition-transform', expanded && 'rotate-180')"
            aria-hidden="true"
          />
        </template>
        {{ expanded ? 'Hide details' : 'Show details' }}
      </AppButton>
    </div>

    <div v-if="expanded" class="mt-3 space-y-2">
      <!-- Compact summary at the top so common fields are scannable
           without opening the JSON. -->
      <dl
        v-if="apiError"
        class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-muted-foreground"
      >
        <dt class="font-medium">Status</dt>
        <dd class="font-mono">{{ apiError.status || '—' }} ({{ apiError.kind }})</dd>
        <dt class="font-medium">Code</dt>
        <dd class="font-mono">{{ apiError.code }}</dd>
        <template v-if="apiError.method || apiError.url">
          <dt class="font-medium">Request</dt>
          <dd class="break-all font-mono">
            {{ apiError.method ?? '—' }} {{ apiError.url ?? '—' }}
          </dd>
        </template>
        <template v-if="apiError.retryAfter !== undefined">
          <dt class="font-medium">Retry after</dt>
          <dd class="font-mono">{{ apiError.retryAfter }}s</dd>
        </template>
      </dl>

      <div class="relative">
        <pre
          class="max-h-64 overflow-auto rounded-md bg-muted/60 p-3 text-xs leading-relaxed font-mono whitespace-pre-wrap break-all"
          >{{ debugJson }}</pre
        >
        <button
          type="button"
          class="absolute right-2 top-2 inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
          @click="copyDebug"
        >
          <Copy class="size-3.5" aria-hidden="true" />
          {{ copied ? 'Copied' : 'Copy' }}
        </button>
      </div>
    </div>
  </AppCard>
</template>
